import { createReducer } from '@reduxjs/toolkit'

import { PaginationQuery } from '@/constants/pagination'
import { ReportPeriod } from '@/constants/report-period'
import { TopFansOrder } from '@/constants/top-fans-order'
import {
  FansReports,
  TopFans,
} from '@/model/analytics'
import { selectFeatureTimezone } from '@/redux/global-selector'
import { createAppAsyncThunk } from '@/redux/utils'
import { axios } from '@/utils/axios'

const namespace = 'fansReports'

export interface ReadFansReportsQueryParams {
  startAt: Date
  endAt: Date
  period: ReportPeriod
  creatorId?: number
  permissionKey?: string
}

export const readFansReport = createAppAsyncThunk(
  `${namespace}/readFansReports`,
  async (params: ReadFansReportsQueryParams, { getState }) => {
    const { global: { permissionKey } } = getState()
    const finalPermissionKey = params.permissionKey || permissionKey
    const { featureTimezone } = selectFeatureTimezone(getState())
    return await axios.get<FansReports>('/analytics/fans-report', {
      params: {
        ...params,
        permissionKey: finalPermissionKey,
        responseTimezone: featureTimezone,
      },
    })
  },
)

export interface ReadTopFansQueryParams extends PaginationQuery {
  startAt: Date
  endAt: Date
  creatorId?: number
  order: TopFansOrder
  permissionKey?: string
}

export const readTopFans = createAppAsyncThunk(
  `${namespace}/readTopFans`,
  async (params: ReadTopFansQueryParams, { getState }) => {
    const { global: { permissionKey } } = getState()
    const { featureTimezone } = selectFeatureTimezone(getState())
    const finalPermissionKey = params.permissionKey || permissionKey
    return await axios.get<TopFans>('/analytics/top-fans', {
      params: {
        ...params,
        permissionKey: finalPermissionKey,
        responseTimezone: featureTimezone,
      },
    })
  },
)

const initialState: {
  fansReport?: FansReports,
  topFans: TopFans,
} = {
  topFans: {
    grossEarning: { total: 0, rows: [] },
    netEarning: { total: 0, rows: [] },
  },
}

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(readFansReport.fulfilled, (draft, { payload }) => {
    draft.fansReport = payload.data
  })

  builder.addCase(readTopFans.fulfilled, function (draft, { meta, payload }) {
    const { data: { grossEarning, netEarning } } = payload

    draft.topFans.grossEarning.total = grossEarning.total
    draft.topFans.netEarning.total = netEarning.total

    if (meta.arg.pageNum === 1) {
      draft.topFans.grossEarning.rows = grossEarning.rows
      draft.topFans.netEarning.rows = netEarning.rows
    } else {
      draft.topFans.grossEarning.rows.push(...grossEarning.rows)
      draft.topFans.netEarning.rows.push(...netEarning.rows)
    }
  })
})

export default { [namespace]: reducer }

